/**
 * Python check assertions — parsing + the turtle-grading shim.
 *
 * Provides two pieces used by the Pyodide worker
 * (`src/lib/pyodide-worker.client.ts`):
 *   • `parseAssertions(checkCode)` — splits the teacher's check code into
 *     setup lines and individual assertions with pass/fail labels.
 *   • `TURTLE_PRELUDE` — a Python shim that records every turtle move into a
 *     `turtle_path` list so assertions can check the drawn figure
 *     (modulo translation, rotation, and stroke order) without forcing
 *     students to call a particular API.
 *
 * The harness that orchestrates assertion exec lives inside the worker source
 * (see `CHECK_HARNESS_TEMPLATE` in `pyodide-worker.client.ts`); this file
 * stays Python-runtime-free so it can be imported on the main thread.
 */

/**
 * Python shim that installs a minimal in-memory turtle stub into sys.modules
 * BEFORE student code runs, so `import turtle` works inside Pyodide's check
 * harness (Pyodide's stdlib doesn't ship the real turtle module — it
 * depends on tkinter). Every move records its end position into
 * `turtle_path` (list of (x, y, pen_down) tuples). Also exposes
 * `turtle_matches(expected, tolerance, tolerate_rotation)` for
 * rotation/translation-tolerant comparison.
 *
 * Runs only inside the Pyodide check harness. Skulpt (the runtime that
 * actually renders the canvas the student sees on Run) is untouched.
 */
export const TURTLE_PRELUDE = `
# Auto-injected by python-check-runner when student code imports turtle.
# Path entries are 4-tuples: (x, y, pen_down, color). Color is the active pen
# color at the time of the move (None if never set). Old (x, y, pen_down)
# tuples remain readable via index access (turtle_path[i][0..2]) so existing
# checks keep working.
turtle_path = []

import sys as _sys
import math as _math
import types as _types

def _normalize_color(c):
    """Coerce a color to a comparable form: lowercased string, or rounded
    float tuple. Returns None for unrecognised shapes so colour comparison
    fails silently rather than throwing."""
    if c is None:
        return None
    if isinstance(c, str):
        return c.lower().strip()
    if isinstance(c, (tuple, list)):
        try:
            return tuple(round(float(x), 3) for x in c)
        except Exception:
            return None
    return None

class _EduskriptTurtle:
    def __init__(self, *a, **kw):
        self._x = 0.0
        self._y = 0.0
        self._h = 0.0  # heading in degrees, 0 = east
        self._down = True
        self._color = None
        turtle_path.append((0.0, 0.0, True, None))

    def _rec(self):
        turtle_path.append((round(self._x, 6), round(self._y, 6), self._down, self._color))

    def forward(self, d):
        rad = _math.radians(self._h)
        self._x += d * _math.cos(rad)
        self._y += d * _math.sin(rad)
        self._rec()
    fd = forward

    def backward(self, d): self.forward(-d)
    back = backward
    bk = backward

    def right(self, a): self._h = (self._h - a) % 360
    rt = right

    def left(self, a): self._h = (self._h + a) % 360
    lt = left

    def goto(self, x, y=None):
        if y is None:
            x, y = x[0], x[1]
        self._x = float(x); self._y = float(y); self._rec()
    setpos = goto
    setposition = goto

    def setx(self, x): self._x = float(x); self._rec()
    def sety(self, y): self._y = float(y); self._rec()
    def home(self): self._x = self._y = self._h = 0.0; self._rec()

    def penup(self): self._down = False
    pu = penup
    up = penup

    def pendown(self): self._down = True
    pd = pendown
    down = pendown

    def isdown(self): return self._down
    def pos(self): return (self._x, self._y)
    position = pos
    def xcor(self): return self._x
    def ycor(self): return self._y
    def heading(self): return self._h
    def setheading(self, a): self._h = a % 360
    seth = setheading
    def towards(self, x, y=None):
        if y is None: x, y = x[0], x[1]
        return _math.degrees(_math.atan2(y - self._y, x - self._x)) % 360

    def circle(self, radius, extent=None, steps=None):
        if extent is None: extent = 360
        if steps is None: steps = max(int(abs(extent) / 3), 12)
        step_a = extent / steps
        step_d = 2 * radius * _math.sin(_math.radians(abs(step_a)) / 2)
        self.left(step_a / 2)
        for _ in range(steps):
            self.forward(step_d if step_a > 0 else -step_d)
            self.left(step_a)
        self.right(step_a / 2)

    # color/pencolor record the pen color so it can be matched against a
    # solution. Other style methods stay no-ops — they don't affect the path
    # or stroke colour.
    def color(self, *a, **kw):
        if not a:
            return
        # Two positional args = (pen, fill); only pen colour affects strokes.
        # Three positional args = an RGB triple.
        if len(a) == 3:
            self._color = _normalize_color(a)
        else:
            self._color = _normalize_color(a[0])
    def pencolor(self, *a, **kw):
        if not a:
            return
        if len(a) == 3:
            self._color = _normalize_color(a)
        else:
            self._color = _normalize_color(a[0])
    def fillcolor(self, *a, **kw): pass
    def begin_fill(self): pass
    def end_fill(self): pass
    def fill(self, *a): pass
    def width(self, *a): pass
    def pensize(self, *a): pass
    def dot(self, *a, **kw): pass
    def stamp(self): return None
    def clearstamp(self, *a): pass
    def write(self, *a, **kw): pass
    def hideturtle(self): pass
    ht = hideturtle
    def showturtle(self): pass
    st = showturtle
    def isvisible(self): return True
    def speed(self, *a): pass
    def shape(self, *a): pass
    def shapesize(self, *a, **kw): pass
    def tracer(self, *a, **kw): pass
    def update(self): pass
    def reset(self): self._x = self._y = self._h = 0.0; self._down = True
    def clear(self): pass

class _EduskriptScreen:
    def setup(self, *a, **kw): pass
    def title(self, *a, **kw): pass
    def bgcolor(self, *a, **kw): pass
    def bgpic(self, *a, **kw): pass
    def screensize(self, *a, **kw): pass
    def colormode(self, *a, **kw): pass
    def listen(self): pass
    def onkey(self, *a, **kw): pass
    def onkeypress(self, *a, **kw): pass
    def onkeyrelease(self, *a, **kw): pass
    def onclick(self, *a, **kw): pass
    def ontimer(self, *a, **kw): pass
    def update(self): pass
    def tracer(self, *a, **kw): pass
    def mainloop(self): pass
    def done(self): pass
    def exitonclick(self): pass
    def reset(self): pass

# Build a fake turtle module and install it before student code runs.
__fake = _types.ModuleType('turtle')
__fake.Turtle = _EduskriptTurtle
__fake.RawTurtle = _EduskriptTurtle
__fake.Pen = _EduskriptTurtle
__fake.Screen = lambda: _EduskriptScreen()
__fake.TurtleScreen = _EduskriptScreen

# Module-level shortcuts that operate on a single shared default turtle.
__fake._default = None
def __get_default():
    if __fake._default is None:
        __fake._default = _EduskriptTurtle()
    return __fake._default

for __m in ('forward', 'fd', 'backward', 'back', 'bk', 'right', 'rt', 'left', 'lt',
            'goto', 'setpos', 'setposition', 'setx', 'sety', 'home', 'circle',
            'penup', 'pu', 'up', 'pendown', 'pd', 'down', 'isdown',
            'pos', 'position', 'xcor', 'ycor', 'heading', 'setheading', 'seth',
            'color', 'pencolor', 'fillcolor', 'begin_fill', 'end_fill',
            'width', 'pensize', 'dot', 'stamp', 'write',
            'hideturtle', 'ht', 'showturtle', 'st', 'speed', 'shape', 'tracer',
            'update', 'reset', 'clear'):
    def __mk(_name):
        def __fn(*a, **kw):
            return getattr(__get_default(), _name)(*a, **kw)
        return __fn
    setattr(__fake, __m, __mk(__m))

__fake.mainloop = lambda: None
__fake.done = lambda: None
__fake.exitonclick = lambda: None
__fake.bye = lambda: None

_sys.modules['turtle'] = __fake


def _drawn_segments(path):
    """Extract the set of drawn line segments (where the pen was down during
    the move). Each entry is (canonical_seg, color) — canonical means the
    lexicographically smaller endpoint comes first, so direction doesn't
    matter. Color is whatever was active when the move ended; None if
    student never called color()/pencolor().
    """
    segs = []
    for i in range(1, len(path)):
        prev = path[i - 1]
        curr = path[i]
        x0, y0 = prev[0], prev[1]
        x1, y1, down = curr[0], curr[1], curr[2]
        color = curr[3] if len(curr) > 3 else None
        if not down or (x0 == x1 and y0 == y1):
            continue
        p1 = (round(x0, 3), round(y0, 3))
        p2 = (round(x1, 3), round(y1, 3))
        segs.append((tuple(sorted([p1, p2])), color))
    return segs


def _normalize_segs(segs, with_color=False):
    """Translate segments so the bounding-box min corner is at (0, 0).
    \`segs\` is a list of (canonical_seg, color) tuples. With with_color=True,
    color is part of the equality key; otherwise color is dropped."""
    if not segs:
        return frozenset()
    all_pts = [p for s, _c in segs for p in s]
    dx = min(p[0] for p in all_pts)
    dy = min(p[1] for p in all_pts)
    if with_color:
        return frozenset(
            (frozenset((round(p[0] - dx, 3), round(p[1] - dy, 3)) for p in seg), color)
            for seg, color in segs
        )
    return frozenset(
        frozenset((round(p[0] - dx, 3), round(p[1] - dy, 3)) for p in seg)
        for seg, _c in segs
    )


def turtle_matches(expected, tolerate_rotation=True):
    """
    Compare the SET of drawn line segments to expected.
    expected: list of ((x1, y1), (x2, y2)) segments forming the target figure.
    Both sets are normalised so the bounding-box origin is at (0, 0).
    Direction of each segment doesn't matter; order of strokes doesn't matter;
    retracing over an existing segment doesn't count again. With
    tolerate_rotation=True, the four cardinal rotations of expected are tried.
    Color-blind by design — the expected list has no color info. Use
    turtle_solution_matches(..., match_colors=True) for color-aware checks.
    Returns True iff the figure matches.
    """
    actual = _drawn_segments(turtle_path)
    if not actual:
        return False
    target = []
    for seg in expected:
        p1 = (round(seg[0][0], 3), round(seg[0][1], 3))
        p2 = (round(seg[1][0], 3), round(seg[1][1], 3))
        target.append((tuple(sorted([p1, p2])), None))
    a = _normalize_segs(actual)
    if a == _normalize_segs(target):
        return True
    if tolerate_rotation:
        for theta in (90, 180, 270):
            rad = _math.radians(theta)
            c, s = _math.cos(rad), _math.sin(rad)
            rotated = []
            for seg, _c in target:
                rp1 = (seg[0][0] * c - seg[0][1] * s, seg[0][0] * s + seg[0][1] * c)
                rp2 = (seg[1][0] * c - seg[1][1] * s, seg[1][0] * s + seg[1][1] * c)
                rotated.append((tuple(sorted([rp1, rp2])), None))
            if a == _normalize_segs(rotated):
                return True
    return False


def turtle_solution_matches(solution_code, tolerate_rotation=True, match_colors=False):
    """
    Run \`solution_code\` (a string of teacher-written turtle code) in a fresh
    namespace and compare the figure it draws against the student's. Both
    runs share the same instrumented stub, so the comparison is exact and
    teachers don't have to hand-compute segment lists.

    With match_colors=True the comparison also checks that each segment was
    drawn in the same pen color in solution and student code. Use plain
    color names ('red', 'blue', …) consistently on both sides — they're
    compared lowercased; (r, g, b) tuples are also fine but won't match
    against the named version.

    Returns True iff the student's drawn-segment set matches the solution's
    (modulo translation and, optionally, cardinal rotation).
    """
    # Snapshot the student's path so this comparison is non-destructive.
    _student_path = list(turtle_path)
    # Reset for the solution run: clear the path log and the shared module-level
    # default turtle, so the solution starts at (0, 0) facing east just like
    # the student did.
    turtle_path.clear()
    _sys.modules['turtle']._default = None
    try:
        try:
            exec(solution_code, {})
        except Exception:
            return False
        _solution_path = list(turtle_path)
    finally:
        # Restore student state for any later assertions that read turtle_path.
        turtle_path.clear()
        turtle_path.extend(_student_path)
        _sys.modules['turtle']._default = None

    student_segs = _drawn_segments(_student_path)
    solution_segs = _drawn_segments(_solution_path)
    if not student_segs or not solution_segs:
        return False
    a = _normalize_segs(student_segs, with_color=match_colors)
    if a == _normalize_segs(solution_segs, with_color=match_colors):
        return True
    if tolerate_rotation:
        for theta in (90, 180, 270):
            rad = _math.radians(theta)
            c, s = _math.cos(rad), _math.sin(rad)
            rotated = []
            for seg, color in solution_segs:
                rp1 = (seg[0][0] * c - seg[0][1] * s, seg[0][0] * s + seg[0][1] * c)
                rp2 = (seg[1][0] * c - seg[1][1] * s, seg[1][0] * s + seg[1][1] * c)
                rotated.append((tuple(sorted([rp1, rp2])), color))
            if a == _normalize_segs(rotated, with_color=match_colors):
                return True
    return False


def turtle_path_matches(expected, tolerance=1.0, tolerate_rotation=True):
    """
    Strict path comparison — vertex order matters. Use this when the order
    of moves is part of the exercise (e.g. "draw side A first, then side B").
    For "did the student draw the right figure" use turtle_matches instead.
    """
    if not turtle_path or not expected:
        return False
    actual = [(p[0], p[1]) for p in turtle_path]
    target = [(p[0], p[1]) for p in expected]
    if len(actual) != len(target):
        return False
    ax0, ay0 = actual[0]
    tx0, ty0 = target[0]
    a = [(x - ax0, y - ay0) for x, y in actual]
    t = [(x - tx0, y - ty0) for x, y in target]
    def _close(p, q):
        return all(abs(px - qx) <= tolerance and abs(py - qy) <= tolerance
                   for (px, py), (qx, qy) in zip(p, q))
    if _close(a, t):
        return True
    if tolerate_rotation:
        for theta in (90, 180, 270):
            rad = _math.radians(theta)
            c, s = _math.cos(rad), _math.sin(rad)
            rotated = [(x * c - y * s, x * s + y * c) for x, y in t]
            if _close(a, rotated):
                return True
    return False
`

export interface ParsedAssertion {
  line: string
  /** Label shown to the student when the assertion FAILS. */
  failLabel: string
  /** Label shown when the assertion PASSES. Defaults to the fail label. */
  passLabel: string
}

/**
 * Parse assertion lines from check code and extract labels.
 * Lines starting with `assert ` are test cases.
 * Non-assert lines are setup code (runs before assertions).
 *
 * Message syntax:
 *   - `"single message"`        — used for both pass and fail
 *   - `"fail message|pass msg"` — pipe splits fail (left) and pass (right)
 *   - f/r/b string prefixes are accepted; `{var}` placeholders are kept
 *     verbatim and evaluated at runtime as Python f-strings in the
 *     student's namespace (see `__interp` in the harness below). Failures
 *     fall back to `…` so a broken interpolation never shows raw braces.
 *
 * Exported for testing.
 */
export function parseAssertions(checkCode: string): { setupLines: string[]; assertions: ParsedAssertion[] } {
  // Collapse explicit line continuations (a backslash at end of line) so a
  // multi-line `assert <cond>, \\\n    "long message"` becomes one logical
  // line before we split. Without this, each fragment lands in its own file
  // and Python errors with "unexpected EOF while parsing".
  const collapsed = checkCode.replace(/\\\n[ \t]*/g, ' ')
  const lines = collapsed.split('\n')
  const assertions: ParsedAssertion[] = []
  const setupLines: string[] = []

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed || trimmed.startsWith('#')) {
      setupLines.push(line)
      continue
    }

    if (trimmed.startsWith('assert ')) {
      // Extract message: `assert expr, "message"` — also accepts Python string
      // prefixes (f-string is the common case for AI-generated checks).
      const msgMatch = trimmed.match(/,\s*[fFrRbB]{0,2}["'](.+?)["']\s*$/)
      if (msgMatch) {
        const raw = msgMatch[1]
        // Pipe split: "fail|pass". Only the FIRST pipe matters; later pipes
        // stay in the pass message. Without a pipe, both states share the
        // same label (backward compatible).
        const pipeIdx = raw.indexOf('|')
        const failRaw = (pipeIdx === -1 ? raw : raw.slice(0, pipeIdx)).trim()
        const passRaw = (pipeIdx === -1 ? raw : raw.slice(pipeIdx + 1)).trim()
        // When the teacher writes `"fail|"` (empty pass), fall back to the
        // fail label so the row never renders blank on a green checkmark.
        assertions.push({
          line: trimmed,
          failLabel: failRaw,
          passLabel: passRaw.length > 0 ? passRaw : failRaw,
        })
      } else {
        const fallback = `Test ${assertions.length + 1}: \`${trimmed}\``
        assertions.push({ line: trimmed, failLabel: fallback, passLabel: fallback })
      }
    } else {
      setupLines.push(line)
    }
  }

  return { setupLines, assertions }
}

